import { isDealerUser } from "./userTypes";
import { getAuthUserId } from "./userIdentity";

const PENDING_KEY = "ATP_onboarding_tour_pending";
const SEEN_PREFIX = "ATP_onboarding_tour_seen_";

function safeGet(key) {
  try {
    return (
      window.sessionStorage?.getItem(key) ||
      window.localStorage?.getItem(key) ||
      ""
    );
  } catch {
    return "";
  }
}

function safeSet(storage, key, value) {
  try {
    storage?.setItem(key, value);
  } catch {
    /* ignore quota / private mode */
  }
}

function safeRemove(storage, key) {
  try {
    storage?.removeItem(key);
  } catch {
    /* ignore */
  }
}

function roleFromSignupType(userTypeOrAuth) {
  if (isDealerUser(userTypeOrAuth)) return "dealer";
  const raw =
    typeof userTypeOrAuth === "object" && userTypeOrAuth
      ? userTypeOrAuth.type ?? userTypeOrAuth.user?.type
      : userTypeOrAuth;
  const num = Number(raw);
  if (Number.isFinite(num) && num >= 1 && num <= 5) return "dealer";
  return "client";
}

/** Call right after successful signup (before / during auto-login). */
export function markOnboardingTourPending(userTypeOrAuth) {
  const role = roleFromSignupType(userTypeOrAuth);
  safeSet(window.sessionStorage, PENDING_KEY, role);
  safeSet(window.localStorage, PENDING_KEY, role);
}

export function peekOnboardingTourPending() {
  const raw = String(safeGet(PENDING_KEY) || "").toLowerCase();
  if (raw === "dealer" || raw === "client") return raw;
  return "";
}

export function clearOnboardingTourPending() {
  safeRemove(window.sessionStorage, PENDING_KEY);
  safeRemove(window.localStorage, PENDING_KEY);
}

export function hasSeenOnboardingTour(auth) {
  const id = getAuthUserId(auth);
  if (!id) return false;
  return Boolean(safeGet(`${SEEN_PREFIX}${id}`));
}

export function markOnboardingTourSeen(auth) {
  const id = getAuthUserId(auth);
  if (!id) return;
  safeSet(window.localStorage, `${SEEN_PREFIX}${id}`, "1");
  clearOnboardingTourPending();
}

/**
 * Show tour once after signup auto-login for this account type.
 * Audience: "client" | "dealer" | "any"
 */
export function shouldShowOnboardingTour(auth, audience = "any") {
  if (!auth?.token) return false;
  if (hasSeenOnboardingTour(auth)) return false;

  const pending = peekOnboardingTourPending();
  if (!pending) return false;

  // Prefer the signup pending role — auth.type can lag right after auto-login.
  if (audience === "client") {
    return pending === "client" && !isDealerUser(auth);
  }
  if (audience === "dealer") {
    return pending === "dealer";
  }

  return pending === "dealer" || pending === "client";
}

export function resolveTourAudience(auth) {
  return isDealerUser(auth) ? "dealer" : "client";
}
